/**
 * Create API key display modal component for DEX admin/super-admin workflow.
 * Displays newly created API keys with strong warnings about copying.
 * The API key is shown ONCE and cannot be recovered after closing.
 */

import { showToast } from './toast.js';

// Module-level state
let modalState = {
  visible: false,
  keyId: '',
  apiKey: '',
  partyId: '',
  role: '',
  copied: false
};

/**
 * Render the create key modal into a container.
 * @param {HTMLElement} container - Container element to render into
 * @param {Object} state - Modal state
 * @param {boolean} [state.visible=false] - Whether modal is visible
 * @param {string} [state.keyId=''] - The key ID
 * @param {string} [state.apiKey=''] - The actual API key (shown only once!)
 * @param {string} [state.partyId=''] - Associated party ID
 * @param {string} [state.role=''] - Associated role
 * @param {boolean} [state.copied=false] - Whether the key has been copied
 */
export function renderCreateKeyModal(container, state = {}) {
  const {
    visible = false,
    keyId = '',
    apiKey = '',
    partyId = '',
    role = '',
    copied = false
  } = state;

  if (!visible) {
    container.innerHTML = '';
    return;
  }

  // Format role for display
  const roleLabel = formatRoleLabel(role);

  // Copy button state
  const copyButtonText = copied ? 'Copied!' : 'Copy';
  const copyButtonClasses = copied
    ? 'px-3 py-2 bg-green-100 border border-green-300 text-green-700 rounded-md'
    : 'px-3 py-2 bg-gray-100 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-200 transition-colors';

  container.innerHTML = `
    <div
      id="create-key-modal-backdrop"
      class="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-20"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 class="text-lg font-semibold text-gray-900">API Key Created</h2>
          <button
            id="create-key-close-btn"
            class="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <div class="px-6 py-4">
          <!-- Warning box -->
          <div class="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
            <div class="flex items-start">
              <svg class="h-5 w-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
              </svg>
              <div>
                <p class="text-sm font-semibold text-yellow-800">Copy this API key now!</p>
                <p class="text-sm text-yellow-700 mt-1">
                  This key will not be shown again. Store it securely.
                </p>
              </div>
            </div>
          </div>

          <!-- API Key Display -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              API Key
            </label>
            <div class="flex gap-2">
              <input
                type="text"
                id="create-key-value"
                readonly
                class="flex-1 px-3 py-2 text-xs font-mono bg-gray-50 border border-gray-300 rounded-md"
                value="${escapeHtml(apiKey)}"
              />
              <button
                id="create-key-copy-btn"
                class="${copyButtonClasses}"
                title="Copy to clipboard"
              >
                ${copied ? `
                  <svg class="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                ` : `
                  <svg class="h-4 w-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                  </svg>
                `}
              </button>
            </div>
          </div>

          <!-- Key Metadata -->
          <div class="bg-gray-50 border border-gray-200 rounded-md p-3 mb-4">
            <div class="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span class="text-gray-500">Key ID:</span>
                <span class="ml-1 font-mono text-xs text-gray-700">${escapeHtml(keyId)}</span>
              </div>
              <div>
                <span class="text-gray-500">Party:</span>
                <span class="ml-1 text-gray-700">${escapeHtml(partyId)}</span>
              </div>
              <div class="col-span-2">
                <span class="text-gray-500">Role:</span>
                <span class="ml-1 text-gray-700">${roleLabel}</span>
              </div>
            </div>
          </div>

          <!-- Security reminder -->
          <div class="bg-blue-50 border border-blue-200 rounded-md p-3">
            <p class="text-sm text-blue-800">
              <strong>Security tip:</strong> Store this key in a password manager or secure vault.
              Never commit it to version control or share it publicly.
            </p>
          </div>
        </div>

        <div class="flex justify-end px-6 py-4 border-t border-gray-200">
          <button
            id="create-key-done-btn"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            I've copied the key
          </button>
        </div>
      </div>
    </div>
  `;
}

/**
 * Initialize the create key modal with event handlers.
 * @param {HTMLElement} container - Container element
 * @param {Object} callbacks - Callback functions
 * @param {Function} [callbacks.onClose] - Called when modal is closed
 * @param {Function} [callbacks.onCopy] - Called when key is copied
 * @returns {Function} Cleanup function
 */
export function initCreateKeyModal(container, callbacks = {}) {
  const { onClose, onCopy } = callbacks;

  const cleanupFunctions = [];

  // Close handlers
  const closeBtn = container.querySelector('#create-key-close-btn');
  const doneBtn = container.querySelector('#create-key-done-btn');
  const backdrop = container.querySelector('#create-key-modal-backdrop');

  function handleClose() {
    if (onClose) {
      onClose();
    }
  }

  function handleBackdropClick(e) {
    // For this modal, we intentionally don't close on backdrop click
    // to encourage users to copy the key first
    // Uncomment below to enable backdrop close:
    // if (e.target === backdrop) {
    //   handleClose();
    // }
  }

  if (closeBtn) {
    closeBtn.addEventListener('click', handleClose);
    cleanupFunctions.push(() => closeBtn.removeEventListener('click', handleClose));
  }

  if (doneBtn) {
    doneBtn.addEventListener('click', handleClose);
    cleanupFunctions.push(() => doneBtn.removeEventListener('click', handleClose));
  }

  if (backdrop) {
    backdrop.addEventListener('click', handleBackdropClick);
    cleanupFunctions.push(() => backdrop.removeEventListener('click', handleBackdropClick));
  }

  // Copy button
  const copyBtn = container.querySelector('#create-key-copy-btn');
  const keyInput = container.querySelector('#create-key-value');

  async function handleCopy() {
    if (keyInput && keyInput.value) {
      try {
        await navigator.clipboard.writeText(keyInput.value);
        modalState.copied = true;
        showToast('API key copied to clipboard!', 'success');

        // Re-render to show "Copied!" state
        renderCreateKeyModal(container, modalState);
        initCreateKeyModal(container, callbacks);

        if (onCopy) {
          onCopy();
        }
      } catch (err) {
        // Fallback: select the text for manual copy
        keyInput.select();
        keyInput.setSelectionRange(0, keyInput.value.length);

        // Try document.execCommand as fallback
        try {
          document.execCommand('copy');
          modalState.copied = true;
          showToast('API key copied to clipboard!', 'success');

          // Re-render to show "Copied!" state
          renderCreateKeyModal(container, modalState);
          initCreateKeyModal(container, callbacks);

          if (onCopy) {
            onCopy();
          }
        } catch (fallbackErr) {
          showToast('Please copy the selected text manually', 'info');
        }
      }
    }
  }

  if (copyBtn) {
    copyBtn.addEventListener('click', handleCopy);
    cleanupFunctions.push(() => copyBtn.removeEventListener('click', handleCopy));
  }

  // Keyboard support: Escape to close (but show warning if not copied)
  function handleKeydown(e) {
    if (e.key === 'Escape') {
      if (!modalState.copied) {
        // Show warning before closing
        const confirmClose = window.confirm(
          'Are you sure you want to close? The API key will not be shown again.'
        );
        if (!confirmClose) {
          return;
        }
      }
      handleClose();
    }
  }
  document.addEventListener('keydown', handleKeydown);
  cleanupFunctions.push(() => document.removeEventListener('keydown', handleKeydown));

  // Return combined cleanup
  return function cleanup() {
    cleanupFunctions.forEach((fn) => fn());
  };
}

/**
 * Show the create key modal with key data.
 * @param {HTMLElement} container - Container element
 * @param {Object} keyData - Key data to display
 * @param {string} keyData.keyId - The key ID
 * @param {string} keyData.apiKey - The actual API key
 * @param {string} keyData.partyId - Associated party ID
 * @param {string} keyData.role - Associated role
 * @param {Object} callbacks - Callback functions
 * @returns {Function} Cleanup function
 */
export function showCreateKeyModal(container, keyData, callbacks = {}) {
  // Reset state with provided key data
  modalState = {
    visible: true,
    keyId: keyData.keyId || '',
    apiKey: keyData.apiKey || '',
    partyId: keyData.partyId || '',
    role: keyData.role || '',
    copied: false
  };

  // Render and initialize
  renderCreateKeyModal(container, modalState);
  return initCreateKeyModal(container, callbacks);
}

/**
 * Hide the create key modal.
 * Clears the container content.
 * @param {HTMLElement} container - Container element
 */
export function hideCreateKeyModal(container) {
  modalState = {
    visible: false,
    keyId: '',
    apiKey: '',
    partyId: '',
    role: '',
    copied: false
  };
  container.innerHTML = '';
}

/**
 * Format role for display.
 * @param {string} role - Role identifier
 * @returns {string} Formatted role label
 */
function formatRoleLabel(role) {
  const labels = {
    writer: 'Writer',
    reader: 'Reader',
    admin: 'Admin',
    super_admin: 'Super Admin'
  };
  return labels[role] || role;
}

/**
 * Escape HTML special characters to prevent XSS.
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
